import {useSnackbar} from '../contexts/SnackbarContext';
import {useTranslation} from 'react-i18next';

export interface FileValidationOptions {
    maxSizeInMB?: number;
    allowedTypes?: { name: string; extension: string; }[];
    useSnackbar?: boolean;
}

export interface FileValidationResult {
    isValid: boolean;
    errorMessage?: string;
}

export const useFileValidation = () => {
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const validateFileWithFeedback = (
        file: File | null | undefined,
        options: Omit<FileValidationOptions, 'useSnackbar'> = {}
    ): FileValidationResult => {
        const {
            maxSizeInMB = 100,
            allowedTypes = [
                {name: 'text/plain', extension: 'txt'},
                {name: 'application/pdf', extension: 'pdf'},
                {name: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extension: 'docx'},
                {name: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extension: 'xlsx'},
                {name: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', extension: 'pptx'}
            ],
        } = options;

        if (!file) {
            const errorMessage = t('fileValidation.noFile');
            showSnackbar(errorMessage, 'error');
            return {isValid: false, errorMessage};
        }

        if (!allowedTypes.map(({name}) => name).includes(file.type)) {
            const extensions = allowedTypes.map(({extension}) => `.${extension}`).join(', ');
            const errorMessage = t('fileValidation.unsupportedType', {extensions});
            showSnackbar(errorMessage, 'error');
            return {isValid: false, errorMessage};
        }

        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            const errorMessage = t('fileValidation.fileTooLarge', {maxSize: maxSizeInMB});
            showSnackbar(errorMessage, 'error');
            return {isValid: false, errorMessage};
        }

        return {isValid: true};
    };

    return {validateFile: validateFileWithFeedback};
};
